import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Input, Select, Tag, Space, Button, Modal, Form, DatePicker,
  message, Card, Row, Col, Typography, Popconfirm, Tooltip, Spin,
} from 'antd';
import dayjs from 'dayjs';
import {
  SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, DownloadOutlined, CheckOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { canEdit } from '../utils/auth';
import { palette, STATUS_COLORS, TASK_STATUS_COLORS, ACCENT_PILL, WARN_PILL, INCLUSION_TYPE_COLORS } from '../theme';
import Pill from '../components/Pill';

// Совпадает со списком в UploadPage.tsx (SANCTION_LIST_OPTIONS) — это то же
// самое поле (sanction_program/sanction_list проставляются одним и тем же
// значением при загрузке файла), здесь он используется как фильтр таблицы и
// одновременно определяет спец-формат заголовка экспорта в Excel (см.
// ExportTitleBuilder на бэкенде — только для одной из этих четырёх программ +
// выбранного номера списка есть спец-текст, иначе общий).
const SANCTION_PROGRAM_OPTIONS = [
  { label: 'США (US)', value: 'US' },
  { label: 'Великобритания (UK)', value: 'UK' },
  { label: 'Евросоюз (EU)', value: 'EU' },
  { label: 'ООН (UN)', value: 'UN' },
  { label: 'Россия (РФ)', value: 'RU' },
  { label: 'Секторальные санкции', value: 'SECTOR' },
];

const TASK_TYPE_LABELS: Record<string, string> = {
  duplicate_check: 'Проверка дубля',
  inn_not_found: 'Не найден ИНН',
  data_mismatch: 'Расхождение данных',
};

interface Subsidiary {
  id: number; parent_id: number;
  name_ru: string; name_en: string;
  jurisdiction: string; inn: string;
}

const EntitiesPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const editable = canEdit();
  const [entities, setEntities] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [filters, setFilters] = useState<{
    is_russian?: boolean; has_inn?: boolean; status?: string;
    has_list_number?: boolean; list_number?: string;
    sanction_program?: string; sanction_date?: string;
  }>({});
  const [listNumberOptions, setListNumberOptions] = useState<string[]>([]);
  const [listNumberSuggestion, setListNumberSuggestion] = useState<string | null>(null);
  const [listNumberModal, setListNumberModal] = useState(false);
  const [listNumberValue, setListNumberValue] = useState('');
  const [listNumberLoading, setListNumberLoading] = useState(false);
  const [editModal, setEditModal] = useState<{ visible: boolean; entity: any | null }>({
    visible: false, entity: null,
  });
  const [subsidiaries, setSubsidiaries] = useState<Record<number, Subsidiary[]>>({});
  const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
  // Реальный случай: пагинация по 50 записей, а массовое действие (номер
  // списка, статус, удаление) нужно применить сразу ко ВСЕМ записям по
  // текущему фильтру — не заходить на каждую из ~30 страниц вручную.
  // selectAllMatching=true означает "выбраны все N по фильтру", а не только
  // отмеченные галочками строки текущей страницы; matchingIds — их id,
  // подгружаются через /entities/ids (без пагинации, только id) в момент
  // клика на "Выбрать все N по фильтру".
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [matchingIds, setMatchingIds] = useState<React.Key[]>([]);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [entityTasks, setEntityTasks] = useState<any[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [applyingTaskId, setApplyingTaskId] = useState<number | null>(null);
  const [editForm] = Form.useForm();

  // Общие параметры фильтра — переиспользуются и для постраничной загрузки
  // (fetchData), и для /entities/ids (все id по фильтру, без пагинации).
  const buildFilterParams = useCallback((): any => {
    const params: any = { search };
    if (filters.is_russian !== undefined) params.is_russian = filters.is_russian;
    if (filters.has_inn !== undefined) params.has_inn = filters.has_inn;
    if (filters.status) params.status = filters.status;
    if (filters.has_list_number !== undefined) params.has_list_number = filters.has_list_number;
    if (filters.list_number !== undefined) params.list_number = filters.list_number;
    if (filters.sanction_program !== undefined) params.sanction_program = filters.sanction_program;
    if (filters.sanction_date !== undefined) params.sanction_date = filters.sanction_date;
    return params;
  }, [search, filters]);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params: any = { ...buildFilterParams(), page, page_size: 50 };
    api.get('/entities/', { params }).then(r => {
      setEntities(r.data.items);
      setTotal(r.data.total);
      setSelectedIds([]);
      setSelectAllMatching(false);
      setMatchingIds([]);
    }).catch(() => message.error('Ошибка загрузки')).finally(() => setLoading(false));
  }, [page, buildFilterParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Номер списка формируется В ПРЕДЕЛАХ ОДНОЙ ПРОГРАММЫ — при выбранном
  // фильтре "Программа" в выпадающем списке и в подсказке "следующий номер"
  // должны быть значения только этой программы, а не сквозные по всей базе.
  useEffect(() => {
    api.get('/entities/list-number/values', { params: { sanction_program: filters.sanction_program } })
      .then(r => setListNumberOptions(r.data.values))
      .catch(() => setListNumberOptions([]));
  }, [filters.sanction_program]);

  const loadSubsidiaries = async (parentId: number) => {
    if (subsidiaries[parentId]) return;
    try {
      const res = await api.get(`/entities/${parentId}/subsidiaries`);
      setSubsidiaries(prev => ({ ...prev, [parentId]: res.data }));
    } catch {
      setSubsidiaries(prev => ({ ...prev, [parentId]: [] }));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/entities/${id}`);
      message.success('ЮЛ удалено');
      fetchData();
    } catch { message.error('Ошибка удаления'); }
  };

  const handleEdit = async (values: any) => {
    try {
      const payload = {
        ...values,
        sanction_date: values.sanction_date ? values.sanction_date.format('YYYY-MM-DD') : undefined,
      };
      if (editModal.entity) {
        await api.put(`/entities/${editModal.entity.id}`, payload);
        message.success('Обновлено');
      } else {
        await api.post('/entities/', payload);
        message.success('Добавлено');
      }
      setEditModal({ visible: false, entity: null });
      fetchData();
    } catch { message.error('Ошибка сохранения'); }
  };

  const openEditModal = (rec: any) => {
    setEditModal({ visible: true, entity: rec });
    editForm.resetFields();
    setEntityTasks([]);
    if (!rec) return;
    editForm.setFieldsValue({
      ...rec,
      sanction_date: rec.sanction_date ? dayjs(rec.sanction_date) : undefined,
    });
    setTasksLoading(true);
    api.get('/tasks/', { params: { legal_entity_id: rec.id } })
      .then(r => setEntityTasks(r.data))
      .catch(() => message.error('Ошибка загрузки задач по ЮЛ'))
      .finally(() => setTasksLoading(false));
  };

  const handleApplySuggestion = async (taskId: number, index: number) => {
    setApplyingTaskId(taskId);
    try {
      const res = await api.post(`/tasks/${taskId}/apply-suggestion`, { index });
      message.success('Вариант применён, задача закрыта');
      editForm.setFieldsValue({ inn: res.data.entity_inn });
      setEntityTasks(prev => prev.map(t => (t.id === taskId ? res.data : t)));
      fetchData();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Ошибка применения варианта');
    } finally {
      setApplyingTaskId(null);
    }
  };

  const handleResolveEntityTask = async (taskId: number) => {
    try {
      await api.put(`/tasks/${taskId}/resolve`, { status: 'resolved', resolution: 'Решено из карточки ЮЛ' });
      message.success('Задача закрыта');
      setEntityTasks(prev => prev.filter(t => t.id !== taskId));
    } catch {
      message.error('Ошибка закрытия задачи');
    }
  };

  // Подсказки Dadata живут в описании как хвост после этого маркера — не
  // показываем его дважды, раз варианты уже рендерятся отдельными кнопками.
  const taskMainDescription = (description: string) => {
    const idx = description.indexOf('Возможные совпадения по названию');
    return idx === -1 ? description : description.slice(0, idx).trim();
  };

  // "Выбрано" может означать либо галочки на текущей странице (selectedIds),
  // либо результат "Выбрать все N по фильтру" (matchingIds) — все массовые
  // действия должны применяться к правильному набору в зависимости от режима.
  const effectiveSelectedIds = selectAllMatching ? matchingIds : selectedIds;

  const selectAllAcrossPages = async () => {
    setSelectAllLoading(true);
    try {
      const res = await api.get('/entities/ids', { params: buildFilterParams() });
      setMatchingIds(res.data.ids);
      setSelectAllMatching(true);
    } catch {
      message.error('Ошибка загрузки списка id по фильтру');
    }
    setSelectAllLoading(false);
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setSelectAllMatching(false);
    setMatchingIds([]);
  };

  // Без доступа к консоли браузера пользователь не может сказать, ЧТО
  // именно пошло не так ("Ошибка скачивания" сама по себе ничего не
  // объясняет) — разворачиваем реальную причину прямо во всплывающем
  // уведомлении. responseType:'blob' означает, что тело ошибки (обычно
  // JSON {"detail": "..."}) тоже приходит как Blob, а не готовый объект —
  // его нужно явно прочитать как текст.
  const describeExportError = async (err: any): Promise<string> => {
    if (!err?.response) return 'Ошибка скачивания: сервер не ответил (проверьте соединение)';
    const status = err.response.status;
    let detail = '';
    const data = err.response.data;
    try {
      const text = data instanceof Blob ? await data.text() : (typeof data === 'string' ? data : '');
      detail = text ? (JSON.parse(text).detail ?? text) : (data?.detail ?? '');
    } catch {
      // тело не JSON/не читается — оставляем detail пустым, покажем хотя бы статус
    }
    return `Ошибка скачивания (код ${status})${detail ? ': ' + detail : ''}`;
  };

  // Выгрузка использует уже применённые фильтры таблицы (программа, номер
  // списка, дата внесения и т.д.) — отдельного экрана/модалки для выбора
  // параметров экспорта нет по решению пользователя. Заголовок и имя файла
  // на бэкенде (см. ExportTitleBuilder) автоматически выбирают
  // программо-специфичный текст только когда И программа, И номер списка
  // выбраны одновременно — иначе используется общий текст "выгрузка всех
  // данных". При выборе конкретных записей (onlySelected) остальные фильтры
  // игнорируются бэкендом (entity_ids сужает выборку сильнее любого фильтра).
  const handleExport = async (onlySelected: boolean) => {
    try {
      const params: any = onlySelected
        ? { entity_ids: effectiveSelectedIds.join(',') }
        : buildFilterParams();
      const res = await api.get('/export/excel', { params, responseType: 'blob' });
      const disposition = res.headers['content-disposition'] as string | undefined;
      // Заголовок содержит ДВА варианта имени файла: RFC 2047 ("=?UTF-8?Q?...?=",
      // для старых клиентов) и RFC 5987 ("filename*=UTF-8''...", процентно
      // закодированный). Матчим строго второй — заголовок реального
      // экспорта содержит текст с "50%", и это буквальный "%" внутри
      // RFC 2047-варианта: если случайно захватить его вместо RFC 5987 и
      // передать в decodeURIComponent, оно кидает "URI malformed" (это не
      // валидный %XX), и скачивание падает с ошибкой ещё до создания Blob.
      const match = disposition?.match(/filename\*=UTF-8''([^;]+)/i);
      let filename = 'sanctions_list.xlsx';
      if (match) {
        try { filename = decodeURIComponent(match[1]); } catch { /* оставляем дефолтное имя */ }
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      message.success('Excel скачан');
    } catch (err: any) {
      message.error(await describeExportError(err), 8);
    }
  };

  const handleBulkDelete = async () => {
    setBulkLoading(true);
    try {
      await api.post('/entities/bulk-delete', { entity_ids: effectiveSelectedIds });
      message.success(`Удалено: ${effectiveSelectedIds.length}`);
      fetchData();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Ошибка удаления');
    }
    setBulkLoading(false);
  };

  const handleBulkStatus = async (status: string) => {
    setBulkLoading(true);
    try {
      await api.post('/entities/bulk-status', { entity_ids: effectiveSelectedIds, status });
      message.success(`Обновлено: ${effectiveSelectedIds.length}`);
      fetchData();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Ошибка обновления статуса');
    }
    setBulkLoading(false);
  };

  const openListNumberModal = async () => {
    setListNumberLoading(true);
    try {
      // Подсказка "следующий номер" — в пределах программы, выбранной
      // фильтром "Программа" над таблицей (если он не выбран, бэкенд
      // откатывается на старое сквозное поведение по всей базе).
      const res = await api.get('/entities/list-number/suggest-next', {
        params: { sanction_program: filters.sanction_program },
      });
      const suggestion = res.data.value as string | null;
      setListNumberSuggestion(suggestion);
      setListNumberValue(suggestion || '');
    } catch {
      setListNumberSuggestion(null);
      setListNumberValue('');
    }
    setListNumberLoading(false);
    setListNumberModal(true);
  };

  const handleApplyListNumber = async () => {
    if (!listNumberValue.trim()) {
      message.error('Введите номер списка');
      return;
    }
    setBulkLoading(true);
    try {
      await api.post('/entities/batch-update', { entity_ids: effectiveSelectedIds, list_number: listNumberValue.trim() });
      message.success(`Обновлено: ${effectiveSelectedIds.length}`);
      setListNumberModal(false);
      fetchData();
      api.get('/entities/list-number/values', { params: { sanction_program: filters.sanction_program } })
        .then(r => setListNumberOptions(r.data.values))
        .catch(() => {});
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Ошибка обновления номера списка');
    }
    setBulkLoading(false);
  };

  const buildFullName = (rec: any) => {
    const parts: string[] = [];
    if (rec.name_ru) parts.push(rec.name_ru);
    if (rec.name_en && rec.name_en !== rec.name_ru) parts.push(rec.name_en);
    if (parts.length > 0) return parts.join(' / ');
    // Реальный случай: название компании на китайском/тайском/фарси/
    // корейском без латинской или русской транслитерации хранится ТОЛЬКО в
    // name_original (см. FileProcessingService.sanitizeNames на бэкенде) —
    // без этого фолбэка такая запись показывалась как "(без названия)",
    // хотя реальное название у неё есть, просто на другой письменности.
    return rec.name_original || rec.name_en || '(без названия)';
  };

  const expandedRowRender = (record: any) => {
    const subs = subsidiaries[record.id] || [];
    if (subs.length === 0) {
      return <span style={{ color: palette.muted }}>Нет дочерних компаний</span>;
    }
    return (
      <Table
        dataSource={subs} rowKey="id" size="small" pagination={false}
        columns={[
          { title: 'Название (рус.)', dataIndex: 'name_ru', width: 250 },
          { title: 'Название (англ.)', dataIndex: 'name_en', width: 250 },
          { title: 'Юрисдикция', dataIndex: 'jurisdiction', width: 120 },
          { title: 'ИНН', dataIndex: 'inn', width: 120 },
        ]}
      />
    );
  };

  const columns: any[] = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: 'Имя', key: 'name', width: 300, ellipsis: true,
      // Реальный случай: поиск теперь находит запись и по aliases (см.
      // buildFilterSpec на бэкенде — DRAGON SECURE GMBH находится по
      // запросу "KRADEN"), но само поле aliases раньше нигде не
      // отображалось — совпадение выглядело "непонятно откуда взялось".
      // Показываем под основным именем приглушённым текстом, только если
      // оно есть.
      render: (_: any, rec: any) => (
        <div>
          {editable ? (
            <Typography.Link onClick={() => openEditModal(rec)}>
              {buildFullName(rec)}
            </Typography.Link>
          ) : buildFullName(rec)}
          {rec.aliases && (
            <div style={{ color: palette.muted, fontSize: 12 }}>
              a.k.a. {rec.aliases}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Резидент', dataIndex: 'is_russian', width: 70,
      render: (v: boolean) => v ? <Pill bg={WARN_PILL.bg} fg={WARN_PILL.fg}>Да</Pill> : <Pill bg={palette.border} fg={palette.muted}>Нет</Pill>,
    },
    {
      title: 'Статус', dataIndex: 'status', width: 140,
      render: (s: string) => {
        const info = STATUS_COLORS[s] || { bg: palette.border, fg: palette.muted, label: s };
        return <Pill bg={info.bg} fg={info.fg}>{info.label}</Pill>;
      },
    },
    {
      title: (
        <Tooltip title="Прямое — ЮЛ само является объектом санкций. Косвенное — ЮЛ выявлено как компания, принадлежащая санкционному физлицу (само физлицо отдельной записью не ведётся)">
          Тип включения
        </Tooltip>
      ),
      dataIndex: 'inclusion_type', width: 110,
      render: (v: string) => {
        const info = INCLUSION_TYPE_COLORS[v] || INCLUSION_TYPE_COLORS.direct;
        return <Pill bg={info.bg} fg={info.fg}>{info.label}</Pill>;
      },
    },
    { title: 'ИНН', dataIndex: 'inn', width: 120 },
    { title: 'ОГРН', dataIndex: 'ogrn', width: 130 },
    {
      title: (
        <Tooltip title="Номер из источника, не подошедший под формат ИНН/ОГРН — требует ручной проверки">
          Др. номер
        </Tooltip>
      ),
      dataIndex: 'other_ids', width: 110,
    },
    { title: 'Юрисдикция', dataIndex: 'jurisdiction', width: 120 },
    {
      title: (
        <Tooltip title="Санкционный список, выбранный при загрузке файла — не парсится из документа">
          Программа
        </Tooltip>
      ),
      dataIndex: 'sanction_program', width: 100,
    },
    { title: 'Номер списка', dataIndex: 'list_number', width: 120 },
    {
      title: 'Дата внесения', dataIndex: 'sanction_date', width: 120,
      render: (v: string) => v ? new Date(v).toLocaleDateString('ru-RU') : '—',
    },
    {
      title: 'Дочки', dataIndex: 'subsidiary_count', width: 60,
      render: (v: number) => v > 0 ? <Pill bg={ACCENT_PILL.bg} fg={ACCENT_PILL.fg}>{v}</Pill> : null,
    },
    ...(editable ? [{
      title: 'Действия', key: 'actions', width: 80,
      render: (_: any, rec: any) => (
        <Space>
          <Button icon={<EditOutlined />} size="small"
            onClick={() => openEditModal(rec)} />
          <Button icon={<DeleteOutlined />} size="small" danger
            onClick={() => handleDelete(rec.id)} />
        </Space>
      ),
    }] : []),
  ];

  return (
    <div>
      <div className="eyebrow">Реестр</div>
      <Typography.Title level={3} className="page-title" style={{ marginTop: 0 }}>Юридические лица</Typography.Title>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Input placeholder="Поиск по названию, ИНН, ОГРН..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              allowClear />
          </Col>
          <Col>
            <Select placeholder="Резидент" allowClear style={{ width: 120 }}
              onChange={v => setFilters(f => ({ ...f, is_russian: v }))}
              options={[
                { label: 'Да (Российские)', value: true },
                { label: 'Нет (Иностранные)', value: false },
              ]} />
          </Col>
          <Col>
            <Select placeholder="ИНН" allowClear style={{ width: 100 }}
              onChange={v => setFilters(f => ({ ...f, has_inn: v }))}
              options={[
                { label: 'Есть ИНН', value: true },
                { label: 'Нет ИНН', value: false },
              ]} />
          </Col>
          <Col>
            <Select placeholder="Статус" allowClear style={{ width: 170 }}
              onChange={v => setFilters(f => ({ ...f, status: v }))}
              options={[
                { label: 'Проверено', value: 'active' },
                { label: 'Требует проверки', value: 'manual_review' },
              ]} />
          </Col>
          <Col>
            <Select placeholder="Номер списка" allowClear style={{ width: 160 }}
              value={filters.list_number !== undefined ? filters.list_number : (filters.has_list_number === false ? '__empty__' : undefined)}
              onChange={v => setFilters(f => ({
                ...f,
                has_list_number: v === '__empty__' ? false : undefined,
                list_number: v === '__empty__' || v === undefined ? undefined : v,
              }))}
              onClear={() => setFilters(f => ({ ...f, has_list_number: undefined, list_number: undefined }))}
              options={[
                { label: 'Нет номера списка (пусто)', value: '__empty__' },
                ...listNumberOptions.map(v => ({ label: v, value: v })),
              ]} />
          </Col>
          <Col>
            <Select placeholder="Программа" allowClear style={{ width: 160 }}
              value={filters.sanction_program}
              onChange={v => setFilters(f => ({ ...f, sanction_program: v }))}
              options={SANCTION_PROGRAM_OPTIONS} />
          </Col>
          <Col>
            <Tooltip title="Только для выгрузки: сузить экспорт до записей с конкретной датой внесения в санкционный список">
              <DatePicker placeholder="Дата внесения (для экспорта)" format="DD.MM.YYYY"
                value={filters.sanction_date ? dayjs(filters.sanction_date) : null}
                onChange={d => setFilters(f => ({ ...f, sanction_date: d ? d.format('YYYY-MM-DD') : undefined }))} />
            </Tooltip>
          </Col>
          <Col>
            <Space>
              <Button icon={<DownloadOutlined />} onClick={() => handleExport(false)}>Excel</Button>
              {editable && (
                <Button type="primary" icon={<PlusOutlined />}
                  onClick={() => openEditModal(null)}>Добавить</Button>
              )}
            </Space>
          </Col>
        </Row>
        {selectedIds.length > 0 && !selectAllMatching && selectedIds.length === entities.length && total > entities.length && (
          <Row style={{ marginTop: 12 }}>
            <Col>
              <Button size="small" type="link" loading={selectAllLoading} onClick={selectAllAcrossPages}>
                Выбраны все {entities.length} на этой странице. Выбрать все {total} по фильтру →
              </Button>
            </Col>
          </Row>
        )}
        {effectiveSelectedIds.length > 0 && (
          <Row style={{ marginTop: 12 }}>
            <Col>
              <Space>
                <Typography.Text>
                  Выбрано: {effectiveSelectedIds.length}{selectAllMatching ? ' (все по фильтру)' : ''}
                </Typography.Text>
                <Button size="small" onClick={clearSelection}>Снять выбор</Button>
                <Button size="small" icon={<DownloadOutlined />} onClick={() => handleExport(true)}>
                  Excel (выбранные)
                </Button>
                {editable && (
                  <>
                    <Button size="small" icon={<CheckOutlined />} loading={bulkLoading}
                      onClick={() => handleBulkStatus('active')}>
                      Отметить проверенными
                    </Button>
                    <Button size="small" loading={listNumberLoading} onClick={openListNumberModal}>
                      Проставить номер списка
                    </Button>
                    <Popconfirm
                      title={`Удалить ${effectiveSelectedIds.length} записей?`}
                      okText="Удалить" cancelText="Отмена" okButtonProps={{ danger: true }}
                      onConfirm={handleBulkDelete}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />} loading={bulkLoading}>
                        Удалить
                      </Button>
                    </Popconfirm>
                  </>
                )}
              </Space>
            </Col>
          </Row>
        )}
      </Card>

      <Table
        columns={columns}
        dataSource={entities}
        rowKey="id"
        loading={loading}
        rowSelection={{
          selectedRowKeys: selectAllMatching ? entities.map(e => e.id) : selectedIds,
          onChange: (keys) => {
            setSelectAllMatching(false);
            setMatchingIds([]);
            setSelectedIds(keys);
          },
        }}
        expandable={{
          expandedRowRender,
          onExpand: (expanded: boolean, record: any) => {
            if (expanded && record.subsidiary_count > 0) {
              loadSubsidiaries(record.id);
            }
          },
          rowExpandable: (record: any) => record.subsidiary_count > 0,
        }}
        pagination={{
          current: page, pageSize: 50, total, showSizeChanger: false,
          onChange: p => setPage(p),
        }}
        scroll={{ x: 1900 }} size="small"
      />

      <Modal
        key={editModal.entity?.id || 'new'}
        title={editModal.entity ? 'Редактировать ЮЛ' : 'Добавить ЮЛ'}
        open={editModal.visible}
        onCancel={() => setEditModal({ visible: false, entity: null })}
        footer={null} width={600}>
        {editModal.entity && (
          <div style={{ marginBottom: 20 }}>
            <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
              Задачи по этому ЮЛ
            </Typography.Text>
            {tasksLoading ? <Spin size="small" /> : (
              entityTasks.filter(t => t.status !== 'resolved' && t.status !== 'dismissed').length === 0 ? (
                <Typography.Text type="secondary">Открытых задач нет</Typography.Text>
              ) : (
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  {entityTasks.filter(t => t.status !== 'resolved' && t.status !== 'dismissed').map(task => {
                    const info = TASK_STATUS_COLORS[task.status] || { bg: palette.border, fg: palette.muted, label: task.status };
                    return (
                      <div key={task.id} style={{ padding: 10, background: palette.cream, borderRadius: 6 }}>
                        <Space style={{ marginBottom: 4 }}>
                          <Tag>{TASK_TYPE_LABELS[task.task_type] || task.task_type}</Tag>
                          <Pill bg={info.bg} fg={info.fg}>{info.label}</Pill>
                        </Space>
                        <div style={{ fontSize: 13, marginBottom: task.suggestions?.length ? 8 : 0 }}>
                          {taskMainDescription(task.description || '')}
                        </div>
                        {task.suggestions && task.suggestions.length > 0 ? (
                          <Space direction="vertical" style={{ width: '100%' }} size={4}>
                            {task.suggestions.map((s: any, idx: number) => (
                              <Button
                                key={idx} size="small" block
                                loading={applyingTaskId === task.id}
                                onClick={() => handleApplySuggestion(task.id, idx)}
                                style={{ textAlign: 'left', height: 'auto', whiteSpace: 'normal' }}
                              >
                                {s.name_short || s.name_full || '?'} · ИНН {s.inn || '?'} · {s.address || 'адрес не указан'}
                              </Button>
                            ))}
                          </Space>
                        ) : (
                          <Button size="small" onClick={() => handleResolveEntityTask(task.id)}>
                            Отметить решённой
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </Space>
              )
            )}
          </div>
        )}
        <Form form={editForm} onFinish={handleEdit} layout="vertical">
          <Form.Item name="name_ru" label="Название (рус.)"><Input /></Form.Item>
          <Form.Item name="name_en" label="Название (англ.)"><Input /></Form.Item>
          <Form.Item name="aliases" label="Также известно как (a.k.a.)"
            tooltip="Другие наименования компании из санкционного списка. Участвуют в поиске по ЮЛ. Через «; »">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="inn" label="ИНН"><Input /></Form.Item>
          <Form.Item name="ogrn" label="ОГРН"><Input /></Form.Item>
          <Form.Item name="jurisdiction" label="Юрисдикция"><Input /></Form.Item>
          <Form.Item name="address" label="Адрес"><Input.TextArea rows={2} /></Form.Item>
          {/* Реальный случай: номер списка теперь жёстко привязан к программе
              и проставляется автоматически при загрузке (США → 1.1, ЕС → 1.2,
              Великобритания → 1.3, ООН → 1.4) — но записи, загруженные ДО
              введения этого правила, могли остаться без номера или со
              свободным текстом. Выпадающий список вместо произвольного
              текстового поля не даёт вручную ввести значение вне этих
              четырёх — на случай точечной донастройки старых записей. */}
          <Form.Item name="list_number" label="Номер списка">
            <Select allowClear placeholder="Не указан" options={[
              { label: '1.1', value: '1.1' },
              { label: '1.2', value: '1.2' },
              { label: '1.3', value: '1.3' },
              { label: '1.4', value: '1.4' },
            ]} />
          </Form.Item>
          <Form.Item name="sanction_program" label="Программа"><Input /></Form.Item>
          <Form.Item name="sanction_date" label="Дата внесения в санкционный список">
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item name="inclusion_type" label="Тип включения"
            tooltip="Прямое — ЮЛ само является объектом санкций. Косвенное — выявлено как компания, принадлежащая санкционному физлицу.">
            <Select placeholder="Прямое (по умолчанию)" options={[
              { label: INCLUSION_TYPE_COLORS.direct.label, value: 'direct' },
              { label: INCLUSION_TYPE_COLORS.indirect.label, value: 'indirect' },
            ]} />
          </Form.Item>
          {/* Не только для "Косвенное": реальный случай (ПАО "Мостотрест") —
              компания сама является прямым объектом санкций (своя карточка
              уже существует), но ТА ЖЕ компания где-то ещё в источнике
              указана как принадлежащая санкционному физлицу — в этом случае
              данные о владельце прикрепляются к уже существующей "Прямой"
              записи, не создавая вторую. Поле поэтому показывается всегда,
              не только когда "Тип включения" = "Косвенное". */}
          <Form.Item name="indirect_owner_info" label="Данные собственника-ФЛ (ФИО, дата рождения, адрес)"
            tooltip="Заполняется автоматически при парсинге, если платформа определила, что этим ЮЛ владеет санкционное физлицо — независимо от типа включения.">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item><Button type="primary" htmlType="submit" block>Сохранить</Button></Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Проставить номер списка"
        open={listNumberModal}
        onCancel={() => setListNumberModal(false)}
        onOk={handleApplyListNumber}
        okText="Применить"
        cancelText="Отмена"
        confirmLoading={bulkLoading}
      >
        <Typography.Paragraph>
          {listNumberSuggestion
            ? `Предложенный следующий номер (по максимальному существующему значению): ${listNumberSuggestion}`
            : 'Не удалось предложить номер автоматически — в базе ещё нет ни одного значения номера списка. Введите его вручную.'}
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary">
          Значение будет проставлено выбранным записям ({effectiveSelectedIds.length}). Можно изменить перед применением.
        </Typography.Paragraph>
        <Input
          value={listNumberValue}
          onChange={e => setListNumberValue(e.target.value)}
          placeholder="Например, 1.4"
        />
      </Modal>
    </div>
  );
};

export default EntitiesPage;